export interface CpProtocolApplication {
  description?: string;
}

export interface CpSavedTrialPlot {
  plot?: {
    id?: number | string;
    rows?: number;
    columns?: number;
    isStripPlot?: boolean;
  };
  properties?: {
    biologicals?: {
      treatments?: unknown;
    };
  };
}

export interface CpNormalizedPlotCell {
  plot_group_id: number;
  plot_cell_id: number;
  color?: string;
  products?: unknown[];
}

export interface CpNormalizedApplicationAssignment {
  application_index: number;
  treatments?: Array<{
    treatment_index: number;
    assignments?: CpNormalizedPlotCell[];
  }>;
  untreated?: Array<{
    plot_group_id: number;
    plot_cell_id: number;
    color?: string;
  }>;
}

export interface CpAssignmentInput {
  applications?: CpProtocolApplication[] | null;
  treatments?: unknown;
  trialPlots?: CpSavedTrialPlot[] | null;
  biologicalAssignments?: CpNormalizedApplicationAssignment[] | null;
  isStripMode?: boolean;
}

export type CpAssignment =
  | { kind: "missing"; raw?: unknown }
  | { kind: "untreated"; raw: unknown }
  | { kind: "assigned"; treatmentId: number; raw: unknown }
  | {
      kind: "blocked";
      reason:
        | "multiple-treatments"
        | "unknown-treatment"
        | "incomplete-treatment"
        | "invalid-treatment-data"
        | "inconsistent-treatment-state";
      raw: unknown;
    };

export interface CpAssignmentBlocker {
  applicationIndex: number;
  targetId?: string;
  reason:
    | "multiple-treatments"
    | "unknown-treatment"
    | "incomplete-treatment"
    | "invalid-treatment-data"
    | "inconsistent-treatment-state"
    | "saved-application-without-protocol"
    | "invalid-saved-application-data"
    | "saved-cell-without-design-target";
  raw: unknown;
}

export interface NormalizedCpApplication {
  index: number;
  description?: string;
  assignments: Record<string, CpAssignment>;
}

export interface NormalizedCpTreatment {
  treatmentId: number;
  definition: unknown;
}

export interface NormalizedCpAssignments {
  applications: NormalizedCpApplication[];
  treatments: NormalizedCpTreatment[];
  blockers: CpAssignmentBlocker[];
  /** Original references are retained so unsupported payload shapes are never silently discarded. */
  rawTrialPlots: CpSavedTrialPlot[];
}

interface DesignTarget {
  id: string;
  plotIndex: number;
  cellIndex: number;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const flattenDefinitions = (value: unknown): unknown[] => {
  if (!Array.isArray(value)) return [];
  return value.flatMap((entry) =>
    Array.isArray(entry) ? flattenDefinitions(entry) : [entry]
  );
};

const makeTargets = (trialPlots: CpSavedTrialPlot[]): DesignTarget[] =>
  trialPlots.flatMap((trialPlot, plotIndex) => {
    const { id, rows, columns } = trialPlot.plot ?? {};
    if (
      id == null ||
      typeof rows !== "number" ||
      typeof columns !== "number" ||
      !Number.isInteger(rows) ||
      !Number.isInteger(columns) ||
      rows <= 0 ||
      columns <= 0
    ) {
      return [];
    }

    return Array.from({ length: rows * columns }, (_, cellIndex) => ({
      id: `${id}-${Math.floor(cellIndex / columns)}-${cellIndex % columns}`,
      plotIndex,
      cellIndex,
    }));
  });

const makeMissingAssignments = (
  targets: DesignTarget[]
): Record<string, CpAssignment> => {
  const assignments: Record<string, CpAssignment> = {};
  targets.forEach(({ id }) => {
    assignments[id] = { kind: "missing" };
  });
  return assignments;
};

const normalizeCell = (raw: unknown, treatments: unknown[]): CpAssignment => {
  if (raw == null) return { kind: "missing", raw };
  if (!isRecord(raw)) {
    return { kind: "blocked", reason: "invalid-treatment-data", raw };
  }

  const selected = raw.selected_treatment;
  if (selected != null && !Array.isArray(selected)) {
    return { kind: "blocked", reason: "invalid-treatment-data", raw };
  }
  const selectedIds = (selected ?? []) as unknown[];
  if (selectedIds.some((id) => !Number.isInteger(id) || (id as number) <= 0)) {
    return { kind: "blocked", reason: "invalid-treatment-data", raw };
  }
  if (selectedIds.length > 1) {
    return { kind: "blocked", reason: "multiple-treatments", raw };
  }

  if (selectedIds.length === 0) {
    if (raw.treated === false) return { kind: "untreated", raw };
    if (raw.treated === true) {
      return { kind: "blocked", reason: "incomplete-treatment", raw };
    }
    return { kind: "missing", raw };
  }

  const treatmentId = selectedIds[0] as number;
  if (raw.treated === false) {
    return { kind: "blocked", reason: "inconsistent-treatment-state", raw };
  }
  if (
    treatmentId > treatments.length ||
    !isRecord(treatments[treatmentId - 1])
  ) {
    return { kind: "blocked", reason: "unknown-treatment", raw };
  }

  return { kind: "assigned", treatmentId, raw };
};

export const normalizeCpAssignments = ({
  applications = [],
  treatments,
  trialPlots = [],
  biologicalAssignments,
  isStripMode = false,
}: CpAssignmentInput): NormalizedCpAssignments => {
  const protocolApplications = applications ?? [];
  const sourceTrialPlots = trialPlots ?? [];
  const normalizedAssignments =
    Array.isArray(biologicalAssignments) &&
    biologicalAssignments.some(
      (application) =>
        (application.untreated?.length ?? 0) > 0 ||
        application.treatments?.some(
          (treatment) => (treatment.assignments?.length ?? 0) > 0
        )
    )
      ? biologicalAssignments
      : undefined;
  const protocolTreatments = flattenDefinitions(treatments);
  const normalizedTreatments = protocolTreatments.map(
    (definition, index): NormalizedCpTreatment => ({
      treatmentId: index + 1,
      definition,
    })
  );
  const targets = makeTargets(sourceTrialPlots);
  const blockers: CpAssignmentBlocker[] = [];

  const savedApplicationCounts = sourceTrialPlots.map(
    (trialPlot, plotIndex) => {
      const saved = trialPlot.properties?.biologicals?.treatments;
      if (saved == null) return 0;
      if (!Array.isArray(saved)) {
        blockers.push({
          applicationIndex: 0,
          reason: "invalid-saved-application-data",
          raw: { plotIndex, value: saved },
        });
        return 0;
      }
      return saved.length;
    }
  );
  const savedApplicationCount = Math.max(0, ...savedApplicationCounts);
  const normalizedApplicationCount = Math.max(
    0,
    ...(normalizedAssignments ?? []).map((entry) =>
      Number.isInteger(entry.application_index) && entry.application_index >= 0
        ? entry.application_index + 1
        : 0
    )
  );
  const applicationCount = Math.max(
    protocolApplications.length,
    normalizedApplicationCount,
    savedApplicationCount
  );

  const normalizedApplications = Array.from(
    { length: applicationCount },
    (_, applicationIndex): NormalizedCpApplication => ({
      index: applicationIndex,
      description: protocolApplications[applicationIndex]?.description,
      assignments: makeMissingAssignments(targets),
    })
  );

  sourceTrialPlots.forEach((trialPlot, plotIndex) => {
    const savedApplications = trialPlot.properties?.biologicals?.treatments;
    if (!Array.isArray(savedApplications)) return;

    savedApplications.forEach((savedCells, applicationIndex) => {
      if (applicationIndex >= protocolApplications.length) {
        blockers.push({
          applicationIndex,
          reason: "saved-application-without-protocol",
          raw: savedCells,
        });
      }
      if (!Array.isArray(savedCells)) {
        blockers.push({
          applicationIndex,
          reason: "invalid-saved-application-data",
          raw: savedCells,
        });
        return;
      }

      const groupTargets = targets.filter(
        (target) => target.plotIndex === plotIndex
      );
      savedCells.forEach((raw, cellIndex) => {
        const savedCellIndex = isRecord(raw)
          ? raw.plot_cell_id ?? cellIndex
          : cellIndex;
        const target = groupTargets.find(
          (entry) => entry.cellIndex === savedCellIndex
        );
        if (!target) {
          blockers.push({
            applicationIndex,
            reason: "saved-cell-without-design-target",
            raw,
          });
          return;
        }
        const assignment = normalizeCell(raw, protocolTreatments);
        normalizedApplications[applicationIndex].assignments[target.id] =
          assignment;
        if (assignment.kind === "blocked") {
          blockers.push({
            applicationIndex,
            targetId: target.id,
            reason: assignment.reason,
            raw: assignment.raw,
          });
        }
      });
    });
  });

  if (normalizedAssignments) {
    const findTarget = (
      plotGroupId: number,
      plotCellId: number
    ): DesignTarget | undefined => {
      const directTarget = targets.find(
        (target) =>
          target.plotIndex === plotGroupId && target.cellIndex === plotCellId
      );
      if (directTarget !== undefined || !isStripMode) return directTarget;

      // The API indexes split backend strips as separate plot groups; the UI
      // merges those groups into one row of stripe cells before rendering.
      return targets.find(
        (target) => target.plotIndex === 0 && target.cellIndex === plotGroupId
      );
    };

    normalizedAssignments.forEach((application) => {
      const { application_index: applicationIndex } = application;
      if (
        !Number.isInteger(applicationIndex) ||
        applicationIndex < 0 ||
        !normalizedApplications[applicationIndex]
      ) {
        blockers.push({
          applicationIndex: Number.isInteger(applicationIndex)
            ? applicationIndex
            : 0,
          reason: "saved-application-without-protocol",
          raw: application,
        });
        return;
      }
      if (applicationIndex >= protocolApplications.length) {
        blockers.push({
          applicationIndex,
          reason: "saved-application-without-protocol",
          raw: application,
        });
      }

      application.treatments?.forEach((treatment) => {
        treatment.assignments?.forEach((cell) => {
          const target = findTarget(cell.plot_group_id, cell.plot_cell_id);
          if (!target) {
            blockers.push({
              applicationIndex,
              reason: "saved-cell-without-design-target",
              raw: cell,
            });
            return;
          }

          const assignment = normalizeCell(
            {
              treated: true,
              selected_treatment: [treatment.treatment_index],
              products: cell.products ?? [],
              color: cell.color ?? "",
              plot_cell_id: cell.plot_cell_id,
            },
            protocolTreatments
          );
          normalizedApplications[applicationIndex].assignments[target.id] =
            assignment;
          if (assignment.kind === "blocked") {
            blockers.push({
              applicationIndex,
              targetId: target.id,
              reason: assignment.reason,
              raw: assignment.raw,
            });
          }
        });
      });

      application.untreated?.forEach((cell) => {
        const target = findTarget(cell.plot_group_id, cell.plot_cell_id);
        if (!target) {
          blockers.push({
            applicationIndex,
            reason: "saved-cell-without-design-target",
            raw: cell,
          });
          return;
        }

        normalizedApplications[applicationIndex].assignments[target.id] = {
          kind: "untreated",
          raw: {
            treated: false,
            selected_treatment: null,
            products: null,
            color: cell.color ?? "",
            plot_cell_id: cell.plot_cell_id,
          },
        };
      });
    });
  }

  return {
    applications: normalizedApplications,
    treatments: normalizedTreatments,
    blockers,
    rawTrialPlots: sourceTrialPlots,
  };
};
